import { useCallback, useState } from 'react'

import { CircleDashed, EllipsisVertical } from 'lucide-react'
import { SubmitHandler, FieldValues, useForm } from 'react-hook-form'

import { RevokeInvitationDialogContent } from '~/app/settings/team/members/team-members-page'
import { List, Pill, TextInput, Tooltip } from '~/components/component-library'
import { Dialog } from '~/components/component-library/Dialog'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { Dropdown } from '~/components/component-library/Dropdown'
import {
  ToastErrorIcon,
  ToastSuccessIcon,
} from '~/components/component-library/Toast'
import { UserDisplay } from '~/components/component-library/UserDisplay'
import { StepButtons } from '~/components/create-team/StepButtons'
import { getRecommendedTreasuryThreshold } from '~/components/create-team/steps/4_TreasuryStep'
import { RemoveMemberDialogContent } from '~/components/settings/RemoveMemberDialogContent'
import { useAuth } from '~/context/AuthProvider'
import {
  useActiveOrgSmartAccountsSetup,
  useActiveOrgUpdates,
  useRequiredActiveOrg,
} from '~/hooks/org'
import { useRequiredAuthUser } from '~/hooks/useAuthUser'
import { useServerRequests } from '~/hooks/useRequests'
import { getUserTreasuryPasskeyId } from '~/hooks/useSmartAccountSetupStep'
import {
  AuthUser,
  OrgInvitation,
  OrgMember,
  OrgMemberRole,
  OrgSigner,
  SmartAccountsSetupAccount,
} from '~/types'
import { sendTrackingData } from '~/utils/tracking'

export function OwnersStep({
  stepForward,
  stepBack,
}: {
  stepForward: () => void
  stepBack: () => void
}) {
  const [inviteOwnerDialogIsOpen, setInviteOwnerDialogIsOpen] = useState(false)
  const { serverPostRequest, isProcessing } = useServerRequests()
  const authUser = useRequiredAuthUser()
  const org = useRequiredActiveOrg()
  const owners = org.members.filter((o) => o.role === OrgMemberRole.Owner)
  const { addInvitation, removeInvitation, updateSmartAccountsSetup } =
    useActiveOrgUpdates()
  const setup = useActiveOrgSmartAccountsSetup()

  const { control, handleSubmit, reset } = useForm<{
    invite_email: string
  }>({
    defaultValues: {
      invite_email: '',
    },
  })

  const updateTreasuryThreshold = useCallback(
    async (newOwnerCount: number) => {
      if (!setup) return

      // Prepare a new setup object with a fresh reference.
      const newSetup = { ...setup }

      // Calculate the new recommendation.
      const newRecommendedThreshold =
        getRecommendedTreasuryThreshold(newOwnerCount)

      // Find any existing treasury threshold record.
      const thresholdRecord = setup.thresholds?.find(
        (t) => t.setupAccount === 'TREASURY',
      )

      // Create new threshold record object with updated values.
      const updatedThresholdRecord = {
        id: thresholdRecord?.id ?? 'treasury',
        setupAccount: 'TREASURY' as SmartAccountsSetupAccount,
        threshold: newRecommendedThreshold,
        expectedSigners: thresholdRecord?.expectedSigners ?? 1,
      }

      // Remove existing treasury threshold record if it exists.
      newSetup.thresholds = newSetup.thresholds?.filter(
        (t) => t.setupAccount !== 'TREASURY',
      )

      // Update threshold records - New array instead of mutation to make sure React re-renders.
      newSetup.thresholds = [...newSetup.thresholds, updatedThresholdRecord]

      // Update threshold in database.
      await serverPostRequest({
        path: '/api/org/smart_accounts_setup/set_treasury_threshold',
        params: { orgId: org.id, threshold: newRecommendedThreshold },
        errorMessage: 'Failed to update Treasury approvals',
      })

      // Update the clientside cache.
      updateSmartAccountsSetup(newSetup)
    },
    [org.id, setup, updateSmartAccountsSetup, serverPostRequest],
  )

  const handleRemoveOwner = useCallback(() => {
    // Update the treasury threshold after removing an owner
    updateTreasuryThreshold(owners.length - 1)
  }, [owners.length, updateTreasuryThreshold])

  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    async ({ invite_email: email }) => {
      sendTrackingData('submitInviteOrgMemberModal')

      const result = await serverPostRequest<OrgInvitation>({
        path: '/api/org/user/invite',
        params: {
          orgId: org!.id,
          email: email.trim(),
          role: OrgMemberRole.Owner,
        },
        successMessage: 'Invite sent',
        errorMessage: 'Failed to invite user',
      })

      if (result) {
        if (result.data) {
          /** Update the threshold to the new recommendation */
          updateTreasuryThreshold(owners.length + org.invitations.length + 1)

          /** Add the invitation to the org */
          addInvitation(result.data)

          /** Close the dialog and reset the form */
          setInviteOwnerDialogIsOpen(!inviteOwnerDialogIsOpen)
          reset()
        }
      }
    },
    [
      addInvitation,
      inviteOwnerDialogIsOpen,
      org,
      reset,
      serverPostRequest,
      updateTreasuryThreshold,
      owners.length,
    ],
  )

  return (
    <>
      <List
        label="Owners"
        footer="Owners can invite members and manage accounts"
        cta={{
          label: 'Invite owner',
          onClick: () => setInviteOwnerDialogIsOpen(true),
        }}
        items={[
          ...owners.map((o) =>
            BuildOwnerRow(o, {
              passkeyLen: org.passkeys.filter((p) => p.user?.id === o.id)
                .length,
              authUser,
              treasuryPasskeyId: getUserTreasuryPasskeyId(o.id, org),
              onRemove: handleRemoveOwner,
            }),
          ),
          ...org.invitations.map((o) =>
            BuildInvitedRow({
              invitation: o,
              onRevoke: () => {
                removeInvitation(o.id)
                updateTreasuryThreshold(
                  owners.length + org.invitations.length - 1,
                )
              },
            }),
          ),
        ]}
      />
      <Dialog
        open={inviteOwnerDialogIsOpen}
        onOpenChange={(state) => {
          setInviteOwnerDialogIsOpen(state)
          reset()
        }}
      >
        <SplitsDialogContent
          id="createOrg"
          variant="cta"
          title="Invite owner"
          isLoading={isProcessing}
          actionButton={{
            text: 'Send invite',
            type: 'submit',
            onClick: handleSubmit(onSubmit),
          }}
        >
          <form
            autoComplete={'off'}
            spellCheck="false"
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-6"
          >
            <TextInput
              control={control}
              inputName="invite_email"
              autoFocus
              // Start of Selection
              validationFunc={(text) => {
                const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
                const email = text.trim()

                if (!email) return 'Email is required'
                if (!emailRegex.test(email))
                  return 'Please enter a valid email address'
                if (
                  owners.some((o) => o.email === email) ||
                  org.invitations.some((o) => o.email === email)
                )
                  return 'Email already in use'
                return true
              }}
              placeholder="name@example.com"
            />
          </form>
        </SplitsDialogContent>
      </Dialog>
      <StepButtons
        isDisabled={isProcessing}
        onNextClick={stepForward}
        onBackClick={stepBack}
      />
    </>
  )
}

export const BuildOwnerRow = (
  o: OrgMember,
  context: {
    passkeyLen: number
    authUser: AuthUser
    treasuryPasskeyId: OrgSigner | undefined
    onRemove?: () => void
  },
) => {
  const { passkeyLen, authUser, treasuryPasskeyId, onRemove } = context
  const { setGlobalModalState, closeGlobalModal } = useAuth()
  const isCurrentUser = o.email === authUser.email

  return {
    key: o.email,
    actionIcon: (
      <div className="flex items-center gap-2">
        <OwnerDetailsChecklistTooltip o={o}>
          <div className="flex items-center gap-2">
            {passkeyLen === 0 || !o.displayName || !treasuryPasskeyId ? (
              <Pill variant="danger" label="Missing details" />
            ) : (
              <Pill variant="success" label="Ready" />
            )}
          </div>
        </OwnerDetailsChecklistTooltip>
        {onRemove && !isCurrentUser && (
          <Dropdown
            side="bottom"
            items={[
              {
                content: 'Remove from workspace',
                onClick: () =>
                  setGlobalModalState({
                    isOpen: true,
                    content: (
                      <RemoveMemberDialogContent
                        member={o}
                        closeDialog={closeGlobalModal}
                        onRemove={onRemove}
                      />
                    ),
                  }),
              },
            ]}
          >
            <EllipsisVertical className="h-4 w-4 cursor-pointer text-secondary" />
          </Dropdown>
        )}
      </div>
    ),
    content: <UserDisplay user={o} layout="standard" size="base" />,
  }
}

export const BuildInvitedRow = ({
  invitation,
  onRevoke,
}: {
  invitation: OrgInvitation
  onRevoke?: () => void
}) => {
  const { setGlobalModalState, closeGlobalModal } = useAuth()
  return {
    key: invitation.email,
    content: (
      <div className="flex min-w-0 items-center gap-2">
        <CircleDashed className="h-6 w-6 shrink-0 text-secondary" />
        <span className="line-clamp-1 break-all text-secondary">
          {invitation.email}
        </span>
      </div>
    ),
    actionIcon: (
      <div className="flex items-center gap-2 text-secondary">
        <Pill label="Invited" />
        {onRevoke && (
          <Dropdown
            side="bottom"
            items={[
              {
                content: 'Revoke invitation',
                onClick: () =>
                  setGlobalModalState({
                    isOpen: true,
                    content: (
                      <RevokeInvitationDialogContent
                        invitation={invitation}
                        closeDialog={closeGlobalModal}
                        onRevoke={onRevoke}
                      />
                    ),
                  }),
              },
            ]}
          >
            <EllipsisVertical className="h-4 w-4 cursor-pointer text-secondary" />
          </Dropdown>
        )}
      </div>
    ),
  }
}

const OwnerDetailsChecklistTooltip = ({
  children,
  o,
}: {
  children: React.ReactElement
  o: OrgMember
}) => {
  return <Tooltip content={<OwnerDetailsChecklist o={o} />}>{children}</Tooltip>
}

const OwnerDetailsChecklist = ({ o }: { o: OrgMember }) => {
  const org = useRequiredActiveOrg()
  // requirement #1 — has a display name
  const hasDisplayName = !!o.displayName
  // requirement #2 — has a preferred passkey
  const preferredPasskey = getUserTreasuryPasskeyId(o.id, org)

  return (
    <Tooltip content="Owner details checklist">
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1.5 [&>svg]:size-3">
          {hasDisplayName ? <ToastSuccessIcon /> : <ToastErrorIcon />}
          <span>Has set a display name</span>
        </div>
        <div className="flex items-center gap-1.5 [&>svg]:size-3">
          {preferredPasskey ? <ToastSuccessIcon /> : <ToastErrorIcon />}
          <span>
            Has set a preferred passkey (
            {preferredPasskey?.name ?? 'No preferred passkey found'})
          </span>
        </div>
      </div>
    </Tooltip>
  )
}
